import type { ComponentChildren, JSX } from 'preact';
import { useRef } from 'preact/hooks';
import { t } from '../../i18n/index.js';
import { ShipSprite } from './ShipSprite.js';
import {
  BOARD_SIZE,
  COLUMNS,
  cellLabel,
  type Cell,
  type ShipPlacement,
  type ShotResult,
} from '../../../shared/games/battleship/index.js';

export interface BoardShip {
  ship: ShipPlacement;
  class?: string;
  sunk?: boolean;
}

function renderShip(s: BoardShip) {
  return (
    <div class={`ship-slot ${s.sunk ? 'sunk' : ''} ${s.class ?? ''}`} style={shipStyle(s.ship)}>
      <ShipSprite size={s.ship.size} orientation={s.ship.orientation} />
    </div>
  );
}

export interface BoardMarker extends Cell {
  result: ShotResult;
  latest?: boolean;
}

const pct = (n: number) => `${n * (100 / BOARD_SIZE)}%`;

/** Absolute position of a ship on the board (row 1 at the bottom). */
export function shipStyle(ship: ShipPlacement): JSX.CSSProperties {
  const vertical = ship.orientation === 'v';
  const topRow = vertical ? ship.y + ship.size - 1 : ship.y;
  return {
    left: pct(ship.x),
    top: pct(BOARD_SIZE - 1 - topRow),
    width: pct(vertical ? 1 : ship.size),
    height: pct(vertical ? ship.size : 1),
  };
}

function cellStyle(cell: Cell): JSX.CSSProperties {
  return { left: pct(cell.x), top: pct(BOARD_SIZE - 1 - cell.y), width: pct(1), height: pct(1) };
}

/** Maps a pointer position to a board cell (null outside the board). */
export function cellFromPointer(grid: HTMLElement, clientX: number, clientY: number): Cell | null {
  const r = grid.getBoundingClientRect();
  const col = Math.floor(((clientX - r.left) / r.width) * BOARD_SIZE);
  const rowFromTop = Math.floor(((clientY - r.top) / r.height) * BOARD_SIZE);
  if (col < 0 || col >= BOARD_SIZE || rowFromTop < 0 || rowFromTop >= BOARD_SIZE) return null;
  return { x: col, y: BOARD_SIZE - 1 - rowFromTop };
}

export function Board(props: {
  title?: string;
  ships?: BoardShip[];
  markers?: BoardMarker[];
  /** Cells shaded as forbidden placement area. */
  shaded?: Cell[];
  /** Cells known to be empty (around sunk ships). */
  knownEmpty?: Cell[];
  preview?: { ship: ShipPlacement; valid: boolean } | null;
  /** Target board: cells are buttons. */
  onCellClick?: (cell: Cell) => void;
  isCellDisabled?: (cell: Cell) => boolean;
  /** Placement board: raw pointer events with the cell under the pointer. */
  onPointer?: (type: 'down' | 'move' | 'up' | 'leave', cell: Cell | null, e: PointerEvent) => void;
  class?: string;
  children?: ComponentChildren;
}) {
  const gridRef = useRef<HTMLDivElement>(null);
  const pointer = (type: 'down' | 'move' | 'up' | 'leave') => (e: PointerEvent) => {
    if (!props.onPointer || !gridRef.current) return;
    if (type === 'down') {
      // Keep receiving events while dragging with a finger.
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    }
    props.onPointer(type, cellFromPointer(gridRef.current, e.clientX, e.clientY), e);
  };

  const rows = Array.from({ length: BOARD_SIZE }, (_, i) => BOARD_SIZE - i);
  const cells: JSX.Element[] = [];
  for (let row = BOARD_SIZE - 1; row >= 0; row--) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      const cell = { x: col, y: row };
      if (props.onCellClick) {
        const disabled = props.isCellDisabled?.(cell) ?? false;
        cells.push(
          <button
            class="cell cell-target"
            disabled={disabled}
            aria-label={t.battleship.cell(cellLabel(cell))}
            onClick={() => props.onCellClick!(cell)}
          />,
        );
      } else {
        cells.push(<div class="cell" />);
      }
    }
  }

  return (
    <div class={`board ${props.class ?? ''}`}>
      {props.title && <div class="board-title">{props.title}</div>}
      <div class="board-frame">
        <div class="board-rows" aria-hidden="true">
          {rows.map((r) => (
            <span>{r}</span>
          ))}
        </div>
        <div
          class={`board-grid ${props.onPointer ? 'board-placement' : ''}`}
          ref={gridRef}
          onPointerDown={pointer('down')}
          onPointerMove={pointer('move')}
          onPointerUp={pointer('up')}
          onPointerLeave={pointer('leave')}
        >
          {cells}
          <div class="board-layer" aria-hidden="true">
            {props.shaded?.map((c) => (
              <div class="forbidden" style={cellStyle(c)} />
            ))}
            {props.knownEmpty?.map((c) => (
              <div class="known-empty" style={cellStyle(c)} />
            ))}
            {/* Afloat ships below the shot markers, sunk ships on top so they stay recognisable. */}
            {props.ships?.filter((s) => !s.sunk).map(renderShip)}
            {props.preview && (
              <div class={`ship-slot preview ${props.preview.valid ? 'valid' : 'invalid'}`} style={shipStyle(props.preview.ship)}>
                <ShipSprite size={props.preview.ship.size} orientation={props.preview.ship.orientation} />
              </div>
            )}
            {props.markers?.map((m) => (
              <div class={`marker marker-${m.result} ${m.latest ? 'latest' : ''}`} style={cellStyle(m)} />
            ))}
            {props.ships?.filter((s) => s.sunk).map(renderShip)}
          </div>
        </div>
        <span />
        <div class="board-cols" aria-hidden="true">
          {COLUMNS.split('').map((c) => (
            <span>{c}</span>
          ))}
        </div>
      </div>
      {props.children}
    </div>
  );
}
